import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router";
import { useApiMutation } from "jadro-pico-app/Api/useApiMutation";
import { useApiQuery } from "jadro-pico-app/Api/useApiQuery";
import { apiFetch, ApiError } from "jadro-pico-app/Api/apiFetch";
import { useResource } from "jadro-pico-app/Resources/useResource";
import { useErrorMessage } from "jadro-pico-app/Resources/errors/ErrorResourcesContext";
import { useIsMobile } from "jadro-pico-app/Components/UI/Layouts/AppLayout/IsMobileHook";
import { validateReturnTo } from "jadro-pico/apps/validateReturnTo";
import { PicoAppDto } from "jadro-pico/apps/PicoAppDto";
import { CommonErrorCodeEnum } from "jadro-pico/enums/common/CommonErrorCodeEnum";
import AutoLayout from "jadro-pico-app/Components/UI/Layouts/AutoLayout/AutoLayout";
import {
  AutoLayoutAlignEnum,
  AutoLayoutDirectionEnum,
  AutoLayoutResizingEnum,
} from "jadro-pico-app/Components/UI/Layouts/AutoLayout/AutoLayoutEnums";
import FontText from "jadro-pico-app/Components/UI/FontText/FontText";
import { FontSizeEnum, FontTypeEnum } from "jadro-pico-app/Components/UI/FontText/FontTextEnums";
import TextField from "jadro-pico-app/Components/UI/TextField/TextField";
import { FilledButton } from "jadro-pico-app/Components/UI/Button";
import PicoLogo from "jadro-pico-app/Components/UI/PicoLogo/PicoLogo";
import { ThemeColorEnum } from "jadro-pico-app/Components/UI/Themes/ThemeEnums";
import {
  LoginValidationSchema,
  type LoginRequest,
} from "jadro-pico/validators/auth/LoginValidationSchema";
import { LoginResources } from "../../Resources/LoginResources";
import { QueryKeys } from "../../queryKeys";

const Login: React.FC = () => {
  const t = useResource(LoginResources);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [apiError, setApiError] = useState<string | null>(null);
  const apiErrorMessage = useErrorMessage(apiError ?? "");

  const returnTo = searchParams.get("returnTo");

  const { data: apps } = useApiQuery<PicoAppDto[]>({
    url: `${import.meta.env.VITE_PICO_API_URL}/apps`,
    queryKey: QueryKeys.appsCatalog,
    staleTime: Infinity,
  });
  const allowedOrigins = apps?.map((app) => new URL(app.feUrl).origin) ?? [];

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginValidationSchema),
  });

  const loginMutation = useApiMutation<unknown, LoginRequest & { operationUuid?: string }>({
    url: `${import.meta.env.VITE_PICO_API_URL}/auth/login`,
    method: "POST",
    skipAuthRefresh: true,
  });

  useEffect(() => {
    if (!apps) return;
    let cancelled = false;
    (async () => {
      try {
        const user = await apiFetch(`${import.meta.env.VITE_PICO_API_URL}/auth/user`, {
          allowAnonymous: true,
        });
        if (cancelled || !user) return;
        const target = returnTo && validateReturnTo(returnTo, allowedOrigins);
        if (target) window.location.href = target;
        else navigate("/");
      } catch {
        // probe failed — show form
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [returnTo, apps, navigate]);

  const onSubmit = handleSubmit(async (values) => {
    setApiError(null);
    try {
      await loginMutation.mutateAsync(values);
      const target = returnTo && validateReturnTo(returnTo, allowedOrigins);
      if (target) window.location.assign(target);
      else navigate("/");
    } catch (err) {
      if (err instanceof ApiError) setApiError(err.errorCode);
      else setApiError(CommonErrorCodeEnum.INTERNAL_SERVER_ERROR);
    }
  });

  return (
    <AutoLayout
      $direction={AutoLayoutDirectionEnum.VERTICAL}
      $align={isMobile ? AutoLayoutAlignEnum.TOP_CENTER : AutoLayoutAlignEnum.CENTER}
      $horizontalResizing={AutoLayoutResizingEnum.FILL}
      $verticalResizing={AutoLayoutResizingEnum.FILL}
      $backgroundColor={isMobile ? ThemeColorEnum.SURFACE : ThemeColorEnum.SURFACE_VARIANT}
      $padding={24}
    >
      <AutoLayout
        $direction={
          isMobile ? AutoLayoutDirectionEnum.VERTICAL : AutoLayoutDirectionEnum.HORIZONTAL
        }
        $align={AutoLayoutAlignEnum.CENTER}
        $gap={isMobile ? 32 : 64}
        $padding={isMobile ? 0 : 48}
        $backgroundColor={isMobile ? undefined : ThemeColorEnum.SURFACE}
        $borderRadius={isMobile ? 0 : 28}
        $horizontalResizing={isMobile ? AutoLayoutResizingEnum.FILL : AutoLayoutResizingEnum.HUG}
      >
        <AutoLayout
          $direction={AutoLayoutDirectionEnum.VERTICAL}
          $align={isMobile ? AutoLayoutAlignEnum.TOP_LEFT : undefined}
          $gap={8}
          $width={260}
          $horizontalResizing={
            isMobile ? AutoLayoutResizingEnum.FILL : AutoLayoutResizingEnum.FIXED
          }
        >
          <PicoLogo size={isMobile ? 40 : 64} />
          <FontText
            text={t.title}
            $fontType={FontTypeEnum.HEADLINE}
            $fontSize={FontSizeEnum.LARGE}
            $color={ThemeColorEnum.ON_SURFACE}
          />
          <FontText
            text={t.subtitle}
            $fontType={FontTypeEnum.BODY}
            $fontSize={FontSizeEnum.MEDIUM}
            $color={ThemeColorEnum.ON_SURFACE_VARIANT}
          />
        </AutoLayout>
        <AutoLayout
          as="form"
          onSubmit={onSubmit}
          noValidate
          $direction={AutoLayoutDirectionEnum.VERTICAL}
          $gap={16}
          $width={320}
          $horizontalResizing={
            isMobile ? AutoLayoutResizingEnum.FILL : AutoLayoutResizingEnum.FIXED
          }
        >
          <TextField
            placeholder={t.username}
            autoComplete="username"
            error={errors.username?.message}
            {...register("username")}
          />
          <TextField
            type="password"
            placeholder={t.password}
            autoComplete="current-password"
            error={errors.password?.message}
            {...register("password")}
          />
          {apiError && (
            <FontText
              text={apiErrorMessage}
              $fontType={FontTypeEnum.BODY}
              $fontSize={FontSizeEnum.SMALL}
              $color={ThemeColorEnum.CUSTOM_RED}
            />
          )}
          <AutoLayout
            $direction={AutoLayoutDirectionEnum.HORIZONTAL}
            $align={AutoLayoutAlignEnum.RIGHT}
            $horizontalResizing={AutoLayoutResizingEnum.FILL}
          >
            <FilledButton label={t.login} disabled={isSubmitting} />
          </AutoLayout>
        </AutoLayout>
      </AutoLayout>
    </AutoLayout>
  );
};

export default Login;
